import { AppDispatch } from "@/redux"
import { appActions } from "@/redux/slice/appSlice"
import { BillingAPI } from "@/API/Billing"
import { billingActions } from "@/redux/slice/billingSlice"
import { showNotification } from "./notificationService"
import { TCSBillingTier } from "@/constants/TCS/billing"
import { BillingSubscriptionResponse } from "@/types/billing"
import { normalizeSubscription } from "@/helpers/billingNormalization"

export const getSubscription = (
  token: string,
  orgId: string
) => async (dispatch: AppDispatch) => {
  dispatch(billingActions.resetSubscription())
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.getBillingSubscription(token, orgId)
  if (resp.data != null) {
    const normalized = normalizeSubscription(resp.data as BillingSubscriptionResponse)
    dispatch(billingActions.setSubscription(normalized))
  } else if (resp.data === null) {
    dispatch(billingActions.setSubscription(null))
  } else {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getInvoices = (
  token: string,
  orgId: string,
  loading: boolean = true
) => async (dispatch: AppDispatch) => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.getBillingInvoices(token, orgId)
  if (resp.data?.invoices && Array.isArray(resp.data.invoices)) {
    dispatch(billingActions.setInvoices(resp.data.invoices))
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getOneTimeFees = (
  token: string,
  orgId: string,
  loading: boolean = true
) => async (dispatch: AppDispatch) => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.getOneTimeFees(token, orgId)
  if (resp.data?.fees && Array.isArray(resp.data.fees)) {
    dispatch(billingActions.setOneTimeFees(resp.data.fees))
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const createPortalSession = (
  token: string,
  orgId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.createPortalSession(token, orgId)
  if (!resp.data?.url) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createOneTimeFeeCheckout = (token: string, feeId: string) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.createOneTimeFeeCheckout(token, feeId)
  if (!resp.data?.url) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createCheckoutSession = (
  token: string,
  orgId: string,
  planTier: TCSBillingTier
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.createCheckoutSession(token, orgId, planTier)
  if (!resp.data?.url) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createPlaybookCheckoutSession = (
  token: string,
  orgId: string,
  playbookCount: number
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.createPlaybookCheckoutSession(token, orgId, playbookCount)
  if (!resp.data?.url) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const updatePlaybookCount = (
  token: string,
  orgId: string,
  playbookCount: number
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.updatePlaybookCount(token, orgId, playbookCount)
  if (resp.data) {
    await dispatch(getSubscription(token, orgId))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createOneTimeFee = (
  token: string,
  orgId: string,
  amount: number,
  label: string,
  notes?: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.createOneTimeFee(token, orgId, amount, label, notes)
  if (!resp.data) {
    showNotification(resp, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

/** Fetches playbook quota info; returns API response (no Redux update). Use when you need the raw response. */
export const fetchPlaybookQuotaInfo = (token: string, organizationId: string) => async () => {
  return BillingAPI.getPlaybookQuotaInfo(token, organizationId)
}

export const grantOrRevokeFreePlaybookQuota = (
  token: string,
  organizationId: string,
  quotaAmount: number,
  notes?: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.grantOrRevokeFreePlaybookQuota(token, organizationId, quotaAmount, notes)
  if (!resp.data) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getPricingModelsStats = (token: string) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.getPricingModelsStats(token)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const waiveOneTimeFee = (token: string, feeId: string, notes?: string) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.waiveOneTimeFee(token, feeId, notes)
  if (!resp.data) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const voidOneTimeFee = (token: string, feeId: string, notes?: string) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await BillingAPI.voidOneTimeFee(token, feeId, notes)
  if (!resp.data) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}
